package fr.frux.resources

import fr.frux.core.common.logging.Logger
import kotlinx.coroutines.CancellationException

/**
 * Lecteur de ressources qui, quand [primary] échoue, relit la même ressource par [fallback] (#673).
 *
 * Sur le web, le lecteur de Compose Resources passe par `CacheStorage`, que le navigateur refuse
 * en navigation privée ou quand les données de site sont bloquées. L'erreur (une `SecurityError`
 * du navigateur, donc un Throwable hors hiérarchie Kotlin, comme pour #619) traversait les
 * nouveaux essais puis laissait l'app sur le pré-splash. Le cache n'est qu'une optimisation : la
 * ressource se lit aussi bien par un `fetch` direct, c'est ce que [fallback] fournit.
 *
 * L'échec de [primary] est logué, pas remonté : si [fallback] échoue à son tour, c'est son erreur
 * qui remonte, telle quelle, pour que Sentry compte la cause réelle de l'échec de lecture.
 * L'annulation n'est jamais rattrapée.
 */
class FallbackResourceBytesSource(
    private val primary: ResourceBytesSource,
    private val fallback: ResourceBytesSource,
) : ResourceBytesSource {
    override suspend fun read(path: String): ByteArray =
        withFallback(path, { primary.read(path) }) { fallback.read(path) }

    override suspend fun readPart(
        path: String,
        offset: Long,
        size: Long,
    ): ByteArray =
        withFallback(path, { primary.readPart(path, offset, size) }) { fallback.readPart(path, offset, size) }

    override fun getUri(path: String): String = primary.getUri(path)

    @Suppress("TooGenericExceptionCaught")
    private suspend fun withFallback(
        path: String,
        primaryRead: suspend () -> ByteArray,
        fallbackRead: suspend () -> ByteArray,
    ): ByteArray =
        try {
            primaryRead()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            Logger.i(
                TAG,
                "Resource read failed for $path (${e::class.simpleName}: ${e.message}), reading without cache",
            )
            fallbackRead()
        }

    private companion object {
        const val TAG = "FallbackResourceBytesSource"
    }
}
